import React, { createContext, useContext, useEffect, useState } from "react";
import { MapPin, X, Check } from "lucide-react";

export const CITIES = [
  "Patna", "Ranchi", "Bhubaneswar", "Siliguri", "Gangtok", "Guwahati",
  "Gaya", "Jamshedpur", "Varanasi", "Delhi NCR", "Kolkata", "Dehradun",
];

const CityContext = createContext({ city: "", setCity: () => {}, cities: CITIES });

export function CityProvider({ children }) {
  const [city, setCityState] = useState("");
  const [prompt, setPrompt] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = localStorage.getItem("eventom_city") || "";
    if (saved && CITIES.includes(saved)) setCityState(saved);
    else if (!sessionStorage.getItem("eventom_city_prompt_dismissed")) setPrompt(true);
  }, []);

  const dismissPrompt = () => {
    setPrompt(false);
    try { sessionStorage.setItem("eventom_city_prompt_dismissed", "1"); } catch {}
  };

  const setCity = (c) => {
    if (!CITIES.includes(c)) return;
    setCityState(c);
    try { localStorage.setItem("eventom_city", c); } catch {}
    setPrompt(false);
  };

  return (
    <CityContext.Provider value={{ city, setCity, cities: CITIES }}>
      {children}
      {prompt && <FirstVisitCityPopup onPick={setCity} onClose={dismissPrompt} />}
    </CityContext.Provider>
  );
}

export const useCity = () => useContext(CityContext);

export function CitySelector({ compact = false }) {
  const { city, setCity } = useCity();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" data-testid="city-selector">
      <button onClick={() => setOpen((v) => !v)} data-testid="city-selector-trigger"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border border-white/10 bg-white/5 hover:bg-white/10 text-white">
        <MapPin className="w-3.5 h-3.5 text-lime" />
        {city || "Pick city"}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-2xl border border-white/10 p-1 z-50" data-testid="city-selector-dropdown"
          style={{ background: "rgba(10,10,10,0.92)", backdropFilter: "blur(18px)" }}>
          {CITIES.map((c) => (
            <button key={c} onClick={() => { setCity(c); setOpen(false); }} data-testid={`city-option-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`flex items-center justify-between w-full px-3 py-2 text-xs font-mono uppercase tracking-widest rounded-xl ${city === c ? "bg-lime/20 text-lime" : "text-muted hover:bg-white/5 hover:text-white"}`}>
              {c}
              {city === c && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FirstVisitCityPopup({ onPick, onClose }) {
  return (
    <div className="fixed top-[68px] left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-1rem)] max-w-3xl px-2 pointer-events-none animate-[slideDown_.4s_ease]" data-testid="city-popup">
      <div className="pointer-events-auto glass rounded-2xl px-4 py-3 md:px-5 md:py-3.5 shadow-2xl relative overflow-hidden border border-white/15"
        style={{ background: "linear-gradient(135deg, rgba(212,255,0,0.14), rgba(255,46,154,0.08), rgba(10,10,10,0.94))", backdropFilter: "blur(20px) saturate(160%)" }}>
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-lime shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-sm md:text-base">Pick your city to see the crowd near you.</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-0.5 hidden md:block">We surface events, VIP invites and Genie results for your city.</div>
          </div>
          <button onClick={onClose} data-testid="city-popup-close" title="Not now" className="icon-circle !w-8 !h-8 bg-white/5 shrink-0"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-3 flex gap-1.5 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1" data-testid="city-popup-grid">
          {CITIES.map((c) => (
            <button key={c} onClick={() => onPick(c)} data-testid={`city-popup-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className="whitespace-nowrap px-3 py-1.5 rounded-full bg-white/5 border border-white/12 hover:border-lime hover:bg-lime hover:text-ink transition-all text-[11px] font-mono uppercase tracking-widest shrink-0">
              {c}
            </button>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes slideDown { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }
        html[data-theme="light"] [data-testid="city-popup"] > div { background: linear-gradient(135deg, rgba(212,255,0,0.25), rgba(255,255,255,0.98)) !important; }
      `}</style>
    </div>
  );
}
